import { DXF_MAPPING_ROLE, type DxfEntityKey } from '@frontend/api';

import {
  INTERROGATION_LINE_COLOR,
  INTERROGATION_LINE_WIDTH_PX,
} from '../constants';
import { entityKeyPositions } from './mappingModel';
import { IGNORE_STROKE_HEX } from './roles';
import type { AccentBatch, DrawingModel, RoleIndex } from './types';

const NO_KEYS: ReadonlySet<DxfEntityKey> = new Set();

type Inputs = {
  model: DrawingModel;
  ignoredKeys: ReadonlySet<DxfEntityKey>;
  hiddenLayers: ReadonlySet<number>;
};

/**
 * Builds the line batches that stand in for the drawing's own strokes while
 * viewing interrogation results on a mapped file: every line white, except the
 * ones mapped as ignored, which are grey.
 *
 * Drawn as batches in place of the strokes, the way the role overlay is, because
 * the static strokes are merged per (layer, style) and one entity in them cannot
 * be recoloured on its own.
 *
 * A factory carrying a one-entry cache: the inputs only change when the mode is
 * entered, the mapping changes, or a layer is shown or hidden, and re-flattening
 * the whole drawing on every push would be wasted work.
 */
export const createInterrogationLineBuilder = () => {
  let cached: (Inputs & { batches: AccentBatch[] }) | null = null;

  return (
    model: DrawingModel | null,
    roleIndex: RoleIndex,
    hiddenLayers: ReadonlySet<number>,
  ): AccentBatch[] => {
    if (!model) {
      cached = null;
      return [];
    }
    const ignoredKeys = roleIndex.get(DXF_MAPPING_ROLE.IGNORE) ?? NO_KEYS;
    if (
      cached &&
      cached.model === model &&
      cached.ignoredKeys === ignoredKeys &&
      cached.hiddenLayers === hiddenLayers
    ) {
      return cached.batches;
    }

    const lineKeys = model.entityKeys.filter((key) => !ignoredKeys.has(key));
    const batches: AccentBatch[] = [];
    const ignoredPositions = entityKeyPositions(
      ignoredKeys,
      model,
      hiddenLayers,
    );
    if (ignoredPositions.length > 0) {
      batches.push({
        positions: ignoredPositions,
        color: IGNORE_STROKE_HEX,
        widthPx: INTERROGATION_LINE_WIDTH_PX,
        dash: null,
      });
    }
    const linePositions = entityKeyPositions(lineKeys, model, hiddenLayers);
    if (linePositions.length > 0) {
      batches.push({
        positions: linePositions,
        color: INTERROGATION_LINE_COLOR,
        widthPx: INTERROGATION_LINE_WIDTH_PX,
        dash: null,
      });
    }

    cached = { model, ignoredKeys, hiddenLayers, batches };
    return batches;
  };
};
